// ============================================================
// COMPONENTE: CHECKLIST DE VIAJE INTERACTIVO
// ============================================================
// El usuario añade sus propias tareas o importa sugerencias.
// Datos guardados en backend (API). Organizado por categorías.
// ============================================================

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "../../hooks/UseMediaQuery";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import DescriptionIcon from "@mui/icons-material/Description";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LuggageIcon from "@mui/icons-material/Luggage";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import CategoryIcon from "@mui/icons-material/Category";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// Categorías disponibles con iconos MUI
const CATEGORIES = [
	{ id: "docs", Icon: DescriptionIcon, label: "Documentación" },
	{ id: "bookings", Icon: ConfirmationNumberIcon, label: "Reservas" },
	{ id: "tech", Icon: PhoneIphoneIcon, label: "Tecnología" },
	{ id: "money", Icon: AccountBalanceWalletIcon, label: "Dinero" },
	{ id: "packing", Icon: LuggageIcon, label: "Maleta" },
	{ id: "lastmin", Icon: AccessAlarmIcon, label: "Último momento" },
	{ id: "custom", Icon: CategoryIcon, label: "Personalizado" },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

export const defaultChecklistData = {
	cityId: "generic",
	cityName: "Mi viaje",
	accentColor: "#6366F1",
	accentColorLight: "rgba(99, 102, 241, 0.1)",
	successColor: "#10B981",
	categories: [],
};

export default function TravelChecklist({
	direction,
	checklistData: propsChecklistData,
	projectId,
	api,
}) {
	const isMobile = useMediaQuery("(max-width: 600px)");
	const checklistData = propsChecklistData || defaultChecklistData;
	const {
		cityName,
		accentColor = "#6366F1",
		accentColorLight = "rgba(99, 102, 241, 0.1)",
		successColor = "#10B981",
		categories: suggestedCategories = [],
		dark: isDark = false,
		cardRadius = 16,
		cardShadow = "none",
		cardBorder,
		itemBorder,
	} = checklistData;

	const cardStyle = {
		backgroundColor: isDark ? "#2d2d3a" : "#fff",
		borderRadius: cardRadius,
		padding: 16,
		border: cardBorder || (isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #eee"),
		boxShadow: cardShadow,
	};
	const h4Style = {
		fontFamily: "'Poppins', sans-serif",
		fontSize: 14,
		fontWeight: 700,
		margin: 0,
		...(isDark && { color: "#e2e8f0" }),
	};

	// Estado principal
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showAddForm, setShowAddForm] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [expandedCategories, setExpandedCategories] = useState({});
	const [showSuggestions, setShowSuggestions] = useState(false);

	// Form state
	const [newText, setNewText] = useState("");
	const [newCategory, setNewCategory] = useState("custom");

	// Ref para debounce
	const saveTimeoutRef = useRef(null);

	// ── Cargar items del backend ──
	useEffect(() => {
		if (!projectId || !api) return;
		setLoading(true);
		api
			.get(`/checklist/${projectId}`)
			.then((data) => {
				const loaded = Array.isArray(data) ? data : [];
				setItems(loaded);
				// Auto-expandir primera categoría si hay items
				if (loaded.length > 0) {
					const firstCat = loaded[0]?.category;
					if (firstCat) setExpandedCategories({ [firstCat]: true });
				}
			})
			.catch((err) => {
				console.error("Error cargando checklist:", err);
				const saved = localStorage.getItem(`checklist-${projectId}`);
				if (saved) setItems(JSON.parse(saved));
			})
			.finally(() => setLoading(false));
	}, [projectId, api]);

	// Fallback localStorage
	useEffect(() => {
		if (projectId && items.length > 0) {
			localStorage.setItem(`checklist-${projectId}`, JSON.stringify(items));
		}
	}, [items, projectId]);

	// ── Cálculos ──
	const totals = useMemo(() => {
		const byCategory = {};
		let total = 0;
		let checked = 0;

		items.forEach((item) => {
			const cat = item.category || "custom";
			if (!byCategory[cat]) byCategory[cat] = { total: 0, checked: 0 };
			byCategory[cat].total += 1;
			total += 1;
			if (item.isChecked) {
				checked += 1;
				byCategory[cat].checked += 1;
			}
		});

		const percent = total > 0 ? Math.round((checked / total) * 100) : 0;
		return { byCategory, total, checked, percent };
	}, [items]);

	// ── Agrupar items por categoría ──
	const groupedItems = useMemo(() => {
		const groups = {};
		items.forEach((item) => {
			const cat = item.category || "custom";
			if (!groups[cat]) groups[cat] = [];
			groups[cat].push(item);
		});
		return groups;
	}, [items]);

	// ── Toggle expandir categoría ──
	const toggleCategory = useCallback((catId) => {
		setExpandedCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
	}, []);

	// ── Añadir nuevo item ──
	const handleAddItem = useCallback(async () => {
		if (!newText.trim()) return;

		const text = newText.trim();
		const tempItem = {
			id: `temp-${Date.now()}`,
			category: newCategory,
			text,
			isChecked: false,
			order: items.length,
		};

		setItems((prev) => [...prev, tempItem]);
		setNewText("");
		setShowAddForm(false);
		setExpandedCategories((prev) => ({ ...prev, [newCategory]: true }));

		if (projectId && api) {
			try {
				const saved = await api.post(`/checklist/${projectId}`, {
					category: newCategory,
					text,
				});
				setItems((prev) => prev.map((i) => (i.id === tempItem.id ? saved : i)));
			} catch (err) {
				console.error("Error guardando item:", err);
			}
		}
	}, [newText, newCategory, items.length, projectId, api]);

	// ── Toggle checked ──
	const toggleChecked = useCallback(
		async (id) => {
			setItems((prev) =>
				prev.map((i) => (i.id === id ? { ...i, isChecked: !i.isChecked } : i))
			);

			if (projectId && api && !id.startsWith("temp-")) {
				const item = items.find((i) => i.id === id);
				try {
					await api.put(`/checklist/item/${id}`, {
						isChecked: !item?.isChecked,
					});
				} catch (err) {
					console.error("Error actualizando item:", err);
				}
			}
		},
		[projectId, api, items]
	);

	// ── Editar item inline ──
	const handleEditSave = useCallback(
		async (id, updates) => {
			setItems((prev) =>
				prev.map((i) => (i.id === id ? { ...i, ...updates } : i))
			);
			setEditingId(null);

			if (projectId && api && !id.startsWith("temp-")) {
				clearTimeout(saveTimeoutRef.current);
				saveTimeoutRef.current = setTimeout(async () => {
					try {
						await api.put(`/checklist/item/${id}`, updates);
					} catch (err) {
						console.error("Error actualizando item:", err);
					}
				}, 500);
			}
		},
		[projectId, api]
	);

	// ── Eliminar item ──
	const handleDelete = useCallback(
		async (id) => {
			setItems((prev) => prev.filter((i) => i.id !== id));

			if (projectId && api && !id.startsWith("temp-")) {
				try {
					await api.delete(`/checklist/item/${id}`);
				} catch (err) {
					console.error("Error eliminando item:", err);
				}
			}
		},
		[projectId, api]
	);

	// ── Importar sugerencias ──
	const handleImportSuggestions = useCallback(
		async (categoryData) => {
			const existingTexts = new Set(items.map((i) => i.text.toLowerCase()));
			const newItems = categoryData.items
				.filter((item) => !existingTexts.has(item.text.toLowerCase()))
				.map((item, i) => ({
					id: `temp-${Date.now()}-${i}`,
					category: categoryData.id,
					text: item.text,
					isChecked: false,
					order: items.length + i,
				}));

			if (newItems.length === 0) return;

			setItems((prev) => [...prev, ...newItems]);
			setExpandedCategories((prev) => ({
				...prev,
				[categoryData.id]: true,
			}));

			if (projectId && api) {
				try {
					const saved = await api.post(`/checklist/bulk/${projectId}`, {
						items: newItems.map((i) => ({
							category: i.category,
							text: i.text,
						})),
					});
					// Reemplazar temporales
					if (Array.isArray(saved)) {
						setItems((prev) => {
							const withoutTemps = prev.filter(
								(i) => !i.id.startsWith("temp-")
							);
							return [...withoutTemps, ...saved];
						});
					}
				} catch (err) {
					console.error("Error importando sugerencias:", err);
				}
			}
		},
		[items, projectId, api]
	);

	// ── Importar todas las sugerencias ──
	const handleImportAll = useCallback(async () => {
		const existingTexts = new Set(items.map((i) => i.text.toLowerCase()));
		const allNew = [];
		let idx = 0;

		suggestedCategories.forEach((cat) => {
			cat.items.forEach((item) => {
				if (!existingTexts.has(item.text.toLowerCase())) {
					allNew.push({
						id: `temp-${Date.now()}-${idx}`,
						category: cat.id,
						text: item.text,
						isChecked: false,
						order: items.length + idx,
					});
					idx++;
				}
			});
		});

		if (allNew.length === 0) {
			setShowSuggestions(false);
			return;
		}

		setItems((prev) => [...prev, ...allNew]);
		setShowSuggestions(false);

		// Expandir todas las categorías
		const allCats = {};
		suggestedCategories.forEach((c) => (allCats[c.id] = true));
		setExpandedCategories((prev) => ({ ...prev, ...allCats }));

		if (projectId && api) {
			try {
				const saved = await api.post(`/checklist/bulk/${projectId}`, {
					items: allNew.map((i) => ({
						category: i.category,
						text: i.text,
					})),
				});
				if (Array.isArray(saved)) {
					setItems((prev) => {
						const withoutTemps = prev.filter((i) => !i.id.startsWith("temp-"));
						return [...withoutTemps, ...saved];
					});
				}
			} catch (err) {
				console.error("Error importando todas las sugerencias:", err);
			}
		}
	}, [items, suggestedCategories, projectId, api]);

	// ── Resetear checklist ──
	const handleReset = useCallback(async () => {
		if (!window.confirm("¿Seguro que quieres borrar todo el checklist?"))
			return;

		const idsToDelete = items
			.filter((i) => !i.id.startsWith("temp-"))
			.map((i) => i.id);

		setItems([]);

		if (projectId && api) {
			try {
				await Promise.all(
					idsToDelete.map((id) => api.delete(`/checklist/item/${id}`))
				);
			} catch (err) {
				console.error("Error reseteando checklist:", err);
			}
		}
	}, [items, projectId, api]);

	return (
		<motion.div
			initial={{ opacity: 0, x: direction === "next" ? 100 : -100 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: direction === "next" ? -100 : 100 }}
			transition={{ duration: 0.5 }}
			style={{ height: "100vh", display: "flex", flexDirection: "column" }}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					backgroundColor: isDark ? "#1a1a2e" : "#f7f7f7",
					color: isDark ? "#e2e8f0" : undefined,
					fontFamily: "'Poppins', sans-serif",
				}}
			>
				{/* Header */}
				<div
					style={{
						backgroundColor: accentColor,
						color: "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 8,
					}}
				>
					<AssignmentTurnedInIcon sx={{ fontSize: 20 }} />
					Checklist de viaje {cityName ? `· ${cityName}` : ""}
				</div>

				<div
					style={{
						flex: 1,
						overflowY: "auto",
						padding: "16px",
						display: "flex",
						flexDirection: "column",
						gap: 16,
					}}
				>
					{/* ── Progreso ── */}
					<div
						style={{
							backgroundColor: accentColorLight,
							border: `3px solid ${accentColor}`,
							borderRadius: 20,
							padding: 20,
							textAlign: "center",
						}}
					>
						<p style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#666", margin: 0 }}>
							Progreso del checklist
						</p>
						<h2
							style={{
								fontSize: 36,
								fontWeight: 800,
								color: totals.percent === 100 ? successColor : accentColor,
								margin: "8px 0",
							}}
						>
							{totals.percent}%
						</h2>
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								gap: 24,
								marginTop: 4,
							}}
						>
							<div style={{ textAlign: "center" }}>
								<p
									style={{
										margin: 0,
										fontSize: 18,
										fontWeight: 700,
										color: successColor,
									}}
								>
									{totals.checked}
								</p>
								<p
									style={{
										margin: 0,
										fontSize: 10,
										color: isDark ? "#94a3b8" : "#888",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 4,
									}}
								>
									<CheckCircleIcon sx={{ fontSize: 12 }} /> Completadas
								</p>
							</div>
							<div
								style={{
									width: 1,
									backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
								}}
							/>
							<div style={{ textAlign: "center" }}>
								<p
									style={{
										margin: 0,
										fontSize: 18,
										fontWeight: 700,
										color: "#F59E0B",
									}}
								>
									{totals.total - totals.checked}
								</p>
								<p
									style={{
										margin: 0,
										fontSize: 10,
										color: isDark ? "#94a3b8" : "#888",
									}}
								>
									Pendientes
								</p>
							</div>
						</div>

						{/* Barra de progreso */}
						{totals.total > 0 && (
							<div
								style={{
									height: 10,
									backgroundColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
									borderRadius: 5,
									overflow: "hidden",
									marginTop: 14,
								}}
							>
								<motion.div
									initial={{ width: 0 }}
									animate={{
										width: `${totals.percent}%`,
									}}
									transition={{ duration: 0.5 }}
									style={{
										height: "100%",
										backgroundColor:
											totals.percent === 100 ? successColor : accentColor,
										borderRadius: 5,
									}}
								/>
							</div>
						)}

						{totals.percent === 100 && totals.total > 0 && (
							<p
								style={{
									textAlign: "center",
									marginTop: 10,
									fontSize: 14,
									color: successColor,
									fontWeight: 700,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 6,
									marginBottom: 0,
								}}
							>
								<EmojiEventsIcon sx={{ fontSize: 20 }} /> ¡Estás listo para tu
								viaje!
							</p>
						)}

						{items.length > 0 && (
							<p
								style={{
									margin: "6px 0 0",
									fontSize: 11,
									color: isDark ? "#94a3b8" : "#888",
								}}
							>
								{totals.total} tarea
								{totals.total !== 1 ? "s" : ""}
							</p>
						)}
					</div>

					{/* ── Lista de tareas por categoría ── */}
					{loading ? (
						<div
							style={{
								textAlign: "center",
								padding: 40,
								color: isDark ? "#94a3b8" : "#888",
							}}
						>
							Cargando checklist...
						</div>
					) : items.length === 0 ? (
						<div
							style={{
								...cardStyle,
								textAlign: "center",
								padding: 32,
							}}
						>
							<AssignmentTurnedInIcon
								sx={{
									fontSize: 48,
									color: accentColor,
									mb: 1.5,
									opacity: 0.6,
								}}
							/>
							<p
								style={{
									fontWeight: 600,
									color: isDark ? "#e2e8f0" : "#333",
									marginBottom: 4,
								}}
							>
								Tu checklist está vacío
							</p>
							<p
								style={{
									fontSize: 12,
									color: isDark ? "#94a3b8" : "#888",
									margin: 0,
								}}
							>
								Añade tareas o importa sugerencias para organizar tu viaje
							</p>
						</div>
					) : (
						CATEGORIES.filter((c) => groupedItems[c.id]).map((cat) => {
							const CatIcon = cat.Icon;
							const catData = totals.byCategory[cat.id] || {
								total: 0,
								checked: 0,
							};
							const isExpanded = expandedCategories[cat.id];
							const isComplete =
								catData.total > 0 && catData.checked === catData.total;

							return (
								<div key={cat.id} style={cardStyle}>
									<button
										onClick={() => toggleCategory(cat.id)}
										style={{
											width: "100%",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											border: "none",
											backgroundColor: "transparent",
											cursor: "pointer",
											padding: 0,
										}}
									>
										<h4
											style={{
												...h4Style,
												color: isComplete ? successColor : accentColor,
												display: "flex",
												alignItems: "center",
												gap: 6,
											}}
										>
											<CatIcon sx={{ fontSize: 18 }} />
											{cat.label}
											<span
												style={{
													fontSize: 11,
													fontWeight: 500,
													color: isComplete ? successColor : "#aaa",
												}}
											>
												{catData.checked}/{catData.total}
											</span>
										</h4>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: 6,
											}}
										>
											{isComplete && (
												<CheckCircleIcon
													sx={{
														fontSize: 16,
														color: successColor,
													}}
												/>
											)}
											<ExpandMoreIcon
												sx={{
													fontSize: 20,
													color: "#888",
													transform: isExpanded
														? "rotate(180deg)"
														: "rotate(0deg)",
													transition: "transform 0.2s",
												}}
											/>
										</div>
									</button>

									{/* Barra de progreso mini */}
									<div
										style={{
											height: 4,
											backgroundColor: "#eee",
											borderRadius: 2,
											overflow: "hidden",
											marginTop: 10,
										}}
									>
										<div
											style={{
												height: "100%",
												width:
													catData.total > 0
														? `${(catData.checked / catData.total) * 100}%`
														: "0%",
												backgroundColor: isComplete
													? successColor
													: accentColor,
												borderRadius: 2,
												transition: "width 0.4s ease",
											}}
										/>
									</div>

									{isExpanded && (
										<div
											style={{
												marginTop: 12,
												display: "flex",
												flexDirection: "column",
												gap: 6,
											}}
										>
											{groupedItems[cat.id].map((item) => (
												<ChecklistItemRow
													key={item.id}
													item={item}
													accentColor={accentColor}
													successColor={successColor}
													isDark={isDark}
													itemBorder={itemBorder}
													isEditing={editingId === item.id}
													onEdit={() => setEditingId(item.id)}
													onSave={(updates) => handleEditSave(item.id, updates)}
													onToggle={() => toggleChecked(item.id)}
													onDelete={() => handleDelete(item.id)}
													onCancel={() => setEditingId(null)}
												/>
											))}
										</div>
									)}
								</div>
							);
						})
					)}

					{/* ── Formulario nueva tarea ── */}
					<AnimatePresence>
						{showAddForm && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: 20 }}
								style={{
									...cardStyle,
									border: `2px solid ${accentColor}`,
								}}
							>
								<h4
									style={{
										...h4Style,
										color: accentColor,
										marginBottom: 12,
									}}
								>
									Nueva tarea
								</h4>

								{/* Selector de categoría */}
								<div
									style={{
										display: "flex",
										flexWrap: "wrap",
										gap: 6,
										marginBottom: 12,
									}}
								>
									{CATEGORIES.map((cat) => {
										const CatIcon = cat.Icon;
										return (
											<button
												key={cat.id}
												onClick={() => setNewCategory(cat.id)}
												style={{
													padding: "6px 10px",
													border: `1.5px solid ${
														newCategory === cat.id ? accentColor : "#ddd"
													}`,
													borderRadius: 8,
													backgroundColor:
														newCategory === cat.id ? accentColorLight : "#fff",
													cursor: "pointer",
													fontSize: 11,
													fontWeight: newCategory === cat.id ? 700 : 400,
													color: newCategory === cat.id ? accentColor : "#666",
													transition: "all 0.15s",
													display: "flex",
													alignItems: "center",
													gap: 4,
												}}
											>
												<CatIcon sx={{ fontSize: 14 }} /> {cat.label}
											</button>
										);
									})}
								</div>

								{/* Texto */}
								<input
									type="text"
									placeholder="¿Qué necesitas hacer? (ej: Contratar seguro de viaje)"
									value={newText}
									onChange={(e) => setNewText(e.target.value)}
									onKeyDown={(e) => e.key === "Enter" && handleAddItem()}
									autoFocus
									style={{
										...inputStyle,
										borderColor: newText.trim() ? accentColor : "#ddd",
										marginBottom: 12,
									}}
								/>

								{/* Botones */}
								<div style={{ display: "flex", gap: 8 }}>
									<button
										onClick={handleAddItem}
										disabled={!newText.trim()}
										style={{
											flex: 1,
											padding: 12,
											border: "none",
											borderRadius: 12,
											backgroundColor: newText.trim() ? accentColor : "#ccc",
											color: "#fff",
											fontWeight: 700,
											fontSize: 14,
											cursor: newText.trim() ? "pointer" : "default",
											transition: "all 0.15s",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 6,
										}}
									>
										<CheckIcon sx={{ fontSize: 18 }} /> Añadir tarea
									</button>
									<button
										onClick={() => {
											setShowAddForm(false);
											setNewText("");
										}}
										style={{
											padding: "12px 16px",
											border: "2px solid #ddd",
											borderRadius: 12,
											backgroundColor: "#fff",
											color: "#888",
											fontWeight: 600,
											fontSize: 14,
											cursor: "pointer",
											display: "flex",
											alignItems: "center",
										}}
									>
										<CloseIcon sx={{ fontSize: 18 }} />
									</button>
								</div>
							</motion.div>
						)}
					</AnimatePresence>

					{/* Botón añadir */}
					{!showAddForm && (
						<button
							onClick={() => setShowAddForm(true)}
							style={{
								padding: 14,
								border: `2px dashed ${accentColor}`,
								borderRadius: 16,
								backgroundColor: accentColorLight,
								color: accentColor,
								fontWeight: 700,
								fontSize: 14,
								cursor: "pointer",
								transition: "all 0.2s",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 8,
							}}
						>
							<AddIcon sx={{ fontSize: 22 }} /> Añadir tarea
						</button>
					)}

					{/* ── Sugerencias por destino ── */}
					{suggestedCategories.length > 0 && (
						<div style={cardStyle}>
							<button
								onClick={() => setShowSuggestions(!showSuggestions)}
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									width: "100%",
									border: "none",
									backgroundColor: "transparent",
									cursor: "pointer",
									padding: 0,
								}}
							>
								<h4
									style={{
										...h4Style,
										color: accentColor,
										display: "flex",
										alignItems: "center",
										gap: 6,
									}}
								>
									<AutoAwesomeIcon sx={{ fontSize: 18 }} /> Sugerencias para{" "}
									{cityName}
								</h4>
								<ExpandMoreIcon
									sx={{
										fontSize: 20,
										color: "#888",
										transform: showSuggestions
											? "rotate(180deg)"
											: "rotate(0deg)",
										transition: "transform 0.2s",
									}}
								/>
							</button>
							{showSuggestions && (
								<div
									style={{
										marginTop: 12,
										display: "flex",
										flexDirection: "column",
										gap: 10,
									}}
								>
									{/* Importar todo */}
									<button
										onClick={handleImportAll}
										style={{
											padding: "10px 14px",
											border: `2px solid ${accentColor}`,
											borderRadius: 12,
											backgroundColor: accentColorLight,
											color: accentColor,
											fontWeight: 700,
											fontSize: 12,
											cursor: "pointer",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: 6,
										}}
									>
										<AutoAwesomeIcon sx={{ fontSize: 16 }} /> Importar todas las
										sugerencias
									</button>

									{/* Por categoría */}
									{suggestedCategories.map((cat) => {
										const catMeta = CATEGORY_MAP[cat.id] || {
											Icon: CategoryIcon,
											label: cat.title,
										};
										const CatIcon = catMeta.Icon;
										return (
											<div
												key={cat.id}
												style={{
													backgroundColor: "#f9f9f9",
													borderRadius: 12,
													padding: 12,
												}}
											>
												<div
													style={{
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
														marginBottom: 8,
													}}
												>
													<span
														style={{
															fontSize: 13,
															fontWeight: 600,
															display: "flex",
															alignItems: "center",
															gap: 6,
														}}
													>
														<CatIcon
															sx={{
																fontSize: 16,
																color: accentColor,
															}}
														/>{" "}
														{cat.title || catMeta.label}
													</span>
													<button
														onClick={() => handleImportSuggestions(cat)}
														style={{
															padding: "4px 10px",
															border: `1px solid ${accentColor}`,
															borderRadius: 6,
															backgroundColor: "#fff",
															color: accentColor,
															fontWeight: 600,
															fontSize: 10,
															cursor: "pointer",
															display: "flex",
															alignItems: "center",
															gap: 4,
														}}
													>
														<AddIcon
															sx={{
																fontSize: 12,
															}}
														/>{" "}
														Añadir
													</button>
												</div>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														gap: 4,
													}}
												>
													{cat.items.map((item) => (
														<p
															key={item.id}
															style={{
																margin: 0,
																fontSize: 11,
																color: "#666",
																paddingLeft: 6,
															}}
														>
															• {item.text}
														</p>
													))}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</div>
					)}

					{/* ── Botones de acción ── */}
					{items.length > 0 && (
						<button
							onClick={handleReset}
							style={{
								padding: "10px",
								backgroundColor: "transparent",
								border: "2px solid #eee",
								borderRadius: 12,
								color: "#aaa",
								fontSize: 12,
								cursor: "pointer",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 6,
								transition: "all 0.15s",
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.color = "#EF4444";
								e.currentTarget.style.borderColor = "#EF4444";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.color = "#aaa";
								e.currentTarget.style.borderColor = "#eee";
							}}
						>
							<RestartAltIcon sx={{ fontSize: 16 }} /> Reiniciar checklist
						</button>
					)}

					<p
						style={{
							fontSize: 10,
							color: "#999",
							textAlign: "center",
							padding: "0 16px",
							lineHeight: 1.5,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 6,
						}}
					>
						<SaveIcon sx={{ fontSize: 14 }} /> Tu checklist se guarda
						automáticamente
					</p>
				</div>
			</div>
		</motion.div>
	);
}

// ── Item Row (editable inline) ──
function ChecklistItemRow({
	item,
	accentColor,
	successColor,
	isDark = false,
	itemBorder,
	isEditing,
	onEdit,
	onSave,
	onToggle,
	onDelete,
	onCancel,
}) {
	const [text, setText] = useState(item.text);
	const dimColor = isDark ? "#64748B" : "#aaa";
	const baseText = isDark ? "#e2e8f0" : "#333";
	const iconDim = isDark ? "#4a5568" : "#ccc";
	const rowBorder = itemBorder || (isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #f0f0f0");

	useEffect(() => {
		setText(item.text);
	}, [item, isEditing]);

	const handleSave = () => {
		onSave({ text: text.trim() || item.text });
	};

	if (isEditing) {
		return (
			<div
				style={{
					display: "flex",
					gap: 8,
					alignItems: "center",
				}}
			>
				<input
					type="text"
					value={text}
					onChange={(e) => setText(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleSave()}
					autoFocus
					style={{
						...inputStyle,
						flex: 1,
						fontSize: 13,
						padding: "8px 10px",
						backgroundColor: isDark ? "#1a1a2e" : "#fff",
						color: isDark ? "#e2e8f0" : "#333",
						borderColor: isDark ? "rgba(255,255,255,0.15)" : "#ddd",
					}}
				/>
				<button
					onClick={handleSave}
					style={{
						padding: "8px 12px",
						border: "none",
						borderRadius: 8,
						backgroundColor: accentColor,
						color: "#fff",
						fontWeight: 600,
						fontSize: 12,
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						flexShrink: 0,
					}}
				>
					<CheckIcon sx={{ fontSize: 18 }} />
				</button>
				<button
					onClick={onCancel}
					style={{
						padding: "8px 12px",
						border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #ddd",
						borderRadius: 8,
						backgroundColor: isDark ? "#2d2d3a" : "#fff",
						color: isDark ? "#94a3b8" : "#888",
						fontSize: 12,
						cursor: "pointer",
						display: "flex",
						alignItems: "center",
						flexShrink: 0,
					}}
				>
					<CloseIcon sx={{ fontSize: 18 }} />
				</button>
			</div>
		);
	}

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				padding: "8px 0",
				borderBottom: rowBorder,
				gap: 8,
			}}
		>
			<button
				onClick={onToggle}
				style={{
					width: 24,
					height: 24,
					borderRadius: 6,
					border: `2px solid ${item.isChecked ? successColor : (isDark ? "#4a5568" : "#ccc")}`,
					backgroundColor: item.isChecked ? successColor : "transparent",
					cursor: "pointer",
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					transition: "all 0.2s",
					padding: 0,
				}}
			>
				{item.isChecked && <CheckIcon sx={{ color: "#fff", fontSize: 14 }} />}
			</button>

			<div
				onClick={onEdit}
				style={{
					flex: 1,
					minWidth: 0,
					cursor: "pointer",
				}}
			>
				<p
					style={{
						margin: 0,
						fontSize: 13,
						fontWeight: 500,
						color: item.isChecked ? dimColor : baseText,
						textDecoration: item.isChecked ? "line-through" : "none",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
						transition: "all 0.2s",
					}}
				>
					{item.text}
				</p>
			</div>

			<button
				onClick={onEdit}
				style={{
					width: 28,
					height: 28,
					borderRadius: 8,
					border: "none",
					backgroundColor: "transparent",
					color: iconDim,
					cursor: "pointer",
					fontSize: 14,
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					transition: "color 0.15s",
				}}
				onMouseEnter={(e) => (e.currentTarget.style.color = accentColor)}
				onMouseLeave={(e) => (e.currentTarget.style.color = iconDim)}
			>
				<EditIcon sx={{ fontSize: 16 }} />
			</button>

			<button
				onClick={onDelete}
				style={{
					width: 28,
					height: 28,
					borderRadius: 8,
					border: "none",
					backgroundColor: "transparent",
					color: iconDim,
					cursor: "pointer",
					fontSize: 14,
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					transition: "color 0.15s",
				}}
				onMouseEnter={(e) => (e.currentTarget.style.color = "#EF4444")}
				onMouseLeave={(e) => (e.currentTarget.style.color = iconDim)}
			>
				<DeleteOutlineIcon sx={{ fontSize: 18 }} />
			</button>
		</div>
	);
}

// Estilo compartido para inputs (cardStyle y h4Style son dinámicos según tema)
const inputStyle = {
	width: "100%",
	padding: "10px 12px",
	border: "2px solid #ddd",
	borderRadius: 10,
	fontSize: 14,
	fontFamily: "'Poppins', sans-serif",
	outline: "none",
	transition: "border-color 0.15s",
	boxSizing: "border-box",
};
